import { Place as PlaceIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  darken,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { t } from "i18next";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";
import React, { useMemo, useState } from "react";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMapEvent,
} from "react-leaflet";
import { useNavigate } from "react-router-dom";
import { useSpecialRights } from "../../../context/SpecialRightsContext";
import { iresoftColors } from "../../../theme/ireColors";
import { Place } from "../../../types/place";
import { redIcon } from "../../../utils/leafletIcons";
import { handleCurrentlyReading } from "../../../utils/utils";
import DetailCard from "../DetailCard/DetailCard";
import LocateButton from "../LocateButton/LocateButton";
import { useDetailPlace } from "../../../hooks/useDetailPlace";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface MapSectionProps {
  places: Place[];
  onMapInteraction?: () => void;
}

const MapEvents: React.FC<{ onMapInteraction: () => void }> = ({
  onMapInteraction,
}) => {
  useMapEvent("mousedown", onMapInteraction);
  useMapEvent("wheel", onMapInteraction);
  useMapEvent("dragstart", onMapInteraction);
  useMapEvent("zoomstart", onMapInteraction);
  return null;
};

const MapSection: React.FC<MapSectionProps> = ({
  places,
  onMapInteraction,
}) => {
  const { hasSpecialRights } = useSpecialRights();
  const [search] = useState("");
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const [showDetailCard, setShowDetailCard] = useState(false);
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const { place: selectedPlace, loading } = useDetailPlace(selectedPlaceId, t);

  const filteredPlaces = useMemo(() => {
    return places
      .filter(
        (p) =>
          p.placeName && p.latitude !== undefined && p.longitude !== undefined
      )
      .filter((p) => p.placeName.toLowerCase().includes(search.toLowerCase()));
  }, [places, search]);

  return (
    <Box
      flex={3}
      position="relative"
      p={2}
      pb={0}
      borderRadius={2}
      sx={{ boxShadow: 6 }}
      maxHeight={"65vh"}
    >
      <MapContainer
        center={[49.8, 15.5]}
        zoom={!isSmallScreen ? 7 : 6}
        style={{ width: "100%", height: "99%" }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {filteredPlaces.map((p) => (
          <Marker
            key={p.id}
            position={[p.latitude, p.longitude]}
            icon={redIcon}
            eventHandlers={{
              click: () => setSelectedPlaceId(p.id),
            }}
          >
            <Popup>
              {loading && selectedPlaceId === p.id ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress />
                </Box>
              ) : selectedPlaceId === p.id && selectedPlace ? (
                <Box
                  sx={{ maxWidth: 200, textAlign: "center", cursor: "default" }}
                  data-place-id={selectedPlace.id}
                >
                  {selectedPlace.image && (
                    <Avatar
                      variant="rounded"
                      src={`data:image/jpeg;base64,${selectedPlace.image}`}
                      alt={selectedPlace.placeName}
                      sx={{
                        width: 100,
                        height: 100,
                        mb: 0.5,
                        mt: 3,
                        borderRadius: 2,
                        mx: "auto",
                      }}
                    />
                  )}

                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color={iresoftColors.black60}
                    mt={1}
                    lineHeight={1.1}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={0.5}
                  >
                    <PlaceIcon
                      sx={{ fontSize: 10, color: iresoftColors.black40 }}
                    />
                    {selectedPlace.placeName}
                  </Typography>

                  {selectedPlace.title && (
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      fontStyle="italic"
                      lineHeight={1}
                      mt={1.5}
                    >
                      {selectedPlace.title}
                    </Typography>
                  )}

                  {selectedPlace.description && (
                    <Typography variant="body2" mt={0.25} lineHeight={1}>
                      {selectedPlace.description.length > 20
                        ? `${selectedPlace.description.substring(0, 20)}...`
                        : selectedPlace.description}
                    </Typography>
                  )}

                  <Box mt={0.5}>
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => setShowDetailCard(true)}
                    >
                      {t("pages.welcome.map.showMore")}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{ maxWidth: 200, textAlign: "center", cursor: "default" }}
                >
                  <Typography variant="body2" color="textSecondary">
                    {p.placeName}
                  </Typography>
                </Box>
              )}
            </Popup>
          </Marker>
        ))}

        {onMapInteraction && <MapEvents onMapInteraction={onMapInteraction} />}
        {hasSpecialRights && <LocateButton />}
      </MapContainer>

      {/*    <InputBase
        placeholder={t("pages.welcome.map.search")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        startAdornment={<Search sx={{ mr: 0.7, cursor: "default" }} />}
        sx={{
          position: "absolute",
          top: 30,
          right: 30,
          zIndex: 1000,
          width: 130,
          bgcolor: "background.paper",
          borderRadius: 4,
          boxShadow: 3,
          paddingX: 1.5,
          transition: "width 0.3s ease",
          "&:focus-within": {
            width: !isSmallScreen ? 220 : 180,
          },
        }}
      /> */}

      {hasSpecialRights && (
        <Button
          variant="contained"
          sx={{
            position: "absolute",
            bottom: 35,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            p: !isSmallScreen ? 1.5 : 0.5,
            px: !isSmallScreen ? 2 : 1.3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
            fontWeight: "bold",
            letterSpacing: 1.2,
            backgroundColor: iresoftColors.iresoftRed,
            color: iresoftColors.white,
            borderRadius: 4,
            "&:hover": {
              backgroundColor: darken(iresoftColors.iresoftRed, 0.15),
            },
          }}
          onClick={() => handleCurrentlyReading(navigate)}
        >
          {t("pages.welcome.map.whereAmIReading")}
        </Button>
      )}

      {showDetailCard && selectedPlace && !loading && (
        <DetailCard
          place={selectedPlace}
          onClose={() => setShowDetailCard(false)}
          onNext={() => {}}
          onPrev={() => {}}
        />
      )}
    </Box>
  );
};

export default React.memo(MapSection);
